import { discriminatedUnion, z } from "zod";

import { NavigationItem } from "./navigation";

export const ViewIdValues = [
  "ingestion-review",
  "dashboard",
  "tickets",
  "contractor-table",
  "tasks",
  "bids",
  "materials-orders",
  "invalid-materials-orders",
  "change-orders-submission",
  "contractor-documents",
  "contractor-production",
  "finalCheckOff",
  "finance",
  "ratebook",
  "intro-call",
  "live-quoting",
  "my-tasks",
  "pre-production",
  "quote-review",
  "standard-quoting",
  "upsell-quoting",
  "post-install-issues",
  "operations-coordinator",
  "blocking-tasks",
  "todays-actions",
  "upsells",
  "production-schedule",
  "review-financial-adjustments",
  "review-bids",
  "permitting",
  "remi-labor",
  "project-milestones",
  "insurance-quoting",
  "enterprise-management",
  "all-assignments",
  "my-assignments",
  "org-assignments",
  "my-assignments-pending-estimate",
  "my-assignments-canceled",
  "my-assignments-approved",
  "my-assignments-corrections-requested",
  "my-assignments-pending-approval",
  "my-assignments-comp-estimates",
  "my-assignments-job-sold",
  "my-assignments-job-not-sold",
  "my-assignments-job-started",
  "my-assignments-job-completed",
  "estimating",
  "estimating-approvals",
  "my-estimating-tasks",
  "my-blocking-tasks",
  "my-westhill-tasks",
  "unassigned-westhill-tasks",
  "my-openly-tasks",
  "unassigned-openly-tasks",
  "1099-estimating",
  "live-photo-queue",
  "discount-config",
  "round-robin-pool",
  "estimate-mappings",
  "contractor-score",
  "corrections",
  "partner-success-tasks",
  "appeasement-tasks",
  "vendor-credit-tasks",
  "concession-approvals",
  "itel-tasks",
  "travelers-cleanup",
  "contractor-dash",
] as const;

export const ViewIdSchema = z.enum(ViewIdValues);
export const ViewId = ViewIdSchema.enum;
export type ViewId = z.infer<typeof ViewIdSchema>;

export const ViewIdDisplayNames: Record<ViewId | "unknown", string> = {
  "ingestion-review": "Ingestion Review",
  dashboard: "Projects",
  tickets: "Tickets",
  "contractor-table": "Contractors",
  tasks: "Tasks",
  bids: "Bids",
  "materials-orders": "Materials orders",
  "invalid-materials-orders": "Invalid materials orders",
  "my-tasks": "My tasks",
  "todays-actions": "Today's actions",
  "production-schedule": "Schedule",
  "standard-quoting": "Standard quoting",
  "insurance-quoting": "Insurance quoting",
  "upsell-quoting": "Upsell quoting",
  "live-quoting": "Live quoting",
  "quote-review": "Quote review",
  "intro-call": "Intro call",
  "pre-production": "Work orders",
  "contractor-production": "Contractor production",
  "contractor-dash": "Performance",
  permitting: "Permitting",
  finalCheckOff: "Final check off",
  finance: "Finance",
  ratebook: "Ratebook",
  "change-orders-submission": "Change orders",
  "post-install-issues": "Post-install issues",
  "operations-coordinator": "Project management",
  "blocking-tasks": "Blocking tasks",
  unknown: "Remi App",
  upsells: "Upsells",
  "review-financial-adjustments": "Review financial adjustments",
  "review-bids": "Review bids",
  "contractor-documents": "Contractor documents",
  "remi-labor": "Remi labor",
  "project-milestones": "Milestones",
  "enterprise-management": "Enterprise management",
  "my-assignments": "My assignments",
  "org-assignments": "All assignments",
  "all-assignments": "Insurance",
  "my-assignments-pending-estimate": "My assignments",
  "my-assignments-canceled": "My assignments",
  "my-assignments-approved": "My assignments",
  "my-assignments-corrections-requested": "My assignments",
  "my-assignments-pending-approval": "My assignments",
  "my-assignments-comp-estimates": "My assignments",
  "my-assignments-job-sold": "My assignments",
  "my-assignments-job-not-sold": "My assignments",
  "my-assignments-job-started": "My assignments",
  "my-assignments-job-completed": "My assignments",
  estimating: "Estimating",
  "estimating-approvals": "Estimating approvals",
  "my-estimating-tasks": "My estimating tasks",
  "my-blocking-tasks": "My blocking tasks",
  "my-westhill-tasks": "My Westhill tasks",
  "unassigned-westhill-tasks": "Unassigned Westhill tasks",
  "my-openly-tasks": "My Openly tasks",
  "unassigned-openly-tasks": "Unassigned Openly tasks",
  "1099-estimating": "1099 Estimating",
  "live-photo-queue": "Live photo queue",
  "discount-config": "Discount Config",
  "round-robin-pool": "Round-Robin Pool",
  "estimate-mappings": "Estimate Mappings",
  "contractor-score": "Contractor scores",
  corrections: "Corrections",
  "partner-success-tasks": "Partner Success tasks",
  "appeasement-tasks": "Appeasement tasks",
  "vendor-credit-tasks": "Vendor credit tasks",
  "concession-approvals": "Concession approvals",
  "itel-tasks": "ITEL tasks",
  "travelers-cleanup": "Traveler's clean up",
};

export const ViewIdToNavigationItem: Record<ViewId, NavigationItem> = {
  "ingestion-review": NavigationItem.ACTIONABLES,
  tickets: NavigationItem.TICKETS,
  "my-tasks": NavigationItem.VIEW_MY_TASKS,
  "operations-coordinator": NavigationItem.VIEW_OPERATIONS_COORDINATOR,
  "standard-quoting": NavigationItem.VIEW_STANDARD_QUOTING,
  "upsell-quoting": NavigationItem.VIEW_UPSELL_QUOTING,
  "live-quoting": NavigationItem.VIEW_LIVE_QUOTING,
  "quote-review": NavigationItem.VIEW_QUOTE_REVIEW,
  "intro-call": NavigationItem.VIEW_INTRO_CALL,
  "pre-production": NavigationItem.VIEW_PRE_PRODUCTION,
  "contractor-production": NavigationItem.VIEW_CONTRACTOR_PRODUCTION,
  "contractor-dash": NavigationItem.CONTRACTOR_DASHBOARD,
  finalCheckOff: NavigationItem.VIEW_FINAL_CHECK_OFF,
  finance: NavigationItem.VIEW_FINANCE,
  ratebook: NavigationItem.VIEW_FINANCE,
  "change-orders-submission": NavigationItem.VIEW_CHANGE_ORDERS,
  "post-install-issues": NavigationItem.VIEW_POST_INSTALL_ISSUES,
  "blocking-tasks": NavigationItem.VIEW_BLOCKING_TASKS,
  upsells: NavigationItem.VIEW_UPSELLS,
  "production-schedule": NavigationItem.VIEW_PRODUCTION_SCHEDULE,
  "review-financial-adjustments": NavigationItem.VIEW_REVIEW_FINANCIAL_ADJUSTMENTS,
  "review-bids": NavigationItem.VIEW_REVIEW_BIDS,
  permitting: NavigationItem.VIEW_PERMITTING,
  "contractor-documents": NavigationItem.VIEW_CONTRACTOR_DOCUMENTS,
  "contractor-table": NavigationItem.CONTRACTORS,
  tasks: NavigationItem.TASKS,
  bids: NavigationItem.BIDS,
  dashboard: NavigationItem.PROJECTS,
  "materials-orders": NavigationItem.ADMIN,
  "invalid-materials-orders": NavigationItem.ADMIN,
  "todays-actions": NavigationItem.TODAYS_ACTIONS,
  "remi-labor": NavigationItem.VIEW_REMI_LABOR,
  "project-milestones": NavigationItem.PROJECT_MILESTONES,
  "insurance-quoting": NavigationItem.VIEW_INSURANCE_QUOTING,
  "enterprise-management": NavigationItem.VIEW_ENTERPRISE_MANAGEMENT,
  "my-assignments": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_PENDING_APPROVAL,
  "org-assignments": NavigationItem.VIEW_INSURANCE_ORG_ASSIGNMENTS,
  "all-assignments": NavigationItem.VIEW_INSURANCE_ALL_ASSIGNMENTS,
  "my-assignments-pending-estimate": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_PENDING_ESTIMATE,
  "my-assignments-canceled": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_CANCELED,
  "my-assignments-approved": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_APPROVED,
  "my-assignments-corrections-requested": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_CORRECTIONS_REQUESTED,
  "my-assignments-pending-approval": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_PENDING_APPROVAL,
  "my-assignments-comp-estimates": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_COMP_ESTIMATES,
  "my-assignments-job-sold": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_SOLD,
  "my-assignments-job-not-sold": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_NOT_SOLD,
  "my-assignments-job-started": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_STARTED,
  "my-assignments-job-completed": NavigationItem.VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_COMPLETED,
  estimating: NavigationItem.VIEW_ESTIMATING,
  "estimating-approvals": NavigationItem.VIEW_ESTIMATING_APPROVALS,
  "my-estimating-tasks": NavigationItem.VIEW_MY_ESTIMATING_TASKS,
  "my-blocking-tasks": NavigationItem.VIEW_MY_BLOCKING_TASKS,
  "my-westhill-tasks": NavigationItem.VIEW_MY_WESTHILL_TASKS,
  "unassigned-westhill-tasks": NavigationItem.VIEW_UNASSIGNED_WESTHILL_TASKS,
  "my-openly-tasks": NavigationItem.VIEW_MY_OPENLY_TASKS,
  "unassigned-openly-tasks": NavigationItem.VIEW_UNASSIGNED_OPENLY_TASKS,
  "1099-estimating": NavigationItem.VIEW_1099_ESTIMATING,
  "live-photo-queue": NavigationItem.LIVE_PHOTO_REVIEW,
  "discount-config": NavigationItem.ADMIN,
  "round-robin-pool": NavigationItem.ADMIN,
  "estimate-mappings": NavigationItem.ADMIN,
  "contractor-score": NavigationItem.ADMIN,
  corrections: NavigationItem.VIEW_CORRECTIONS,
  "partner-success-tasks": NavigationItem.VIEW_PARTNER_SUCCESS_TASKS,
  "appeasement-tasks": NavigationItem.VIEW_APPEASEMENTS,
  "vendor-credit-tasks": NavigationItem.VIEW_VENDOR_CREDITS,
  "concession-approvals": NavigationItem.VIEW_CONCESSION_APPROVALS,
  "itel-tasks": NavigationItem.VIEW_ITEL_TASKS,
  "travelers-cleanup": NavigationItem.VIEW_TRAVELERS_CLEANUP,
};

export const sidebarViewIds = [
  ViewId.corrections,
  ViewId["partner-success-tasks"],
  ViewId["appeasement-tasks"],
  ViewId["vendor-credit-tasks"],
  ViewId["concession-approvals"],
  ViewId["project-milestones"],
  ViewId["my-tasks"],
  ViewId["my-westhill-tasks"],
  ViewId["unassigned-westhill-tasks"],
  ViewId["travelers-cleanup"],
  ViewId["my-openly-tasks"],
  ViewId["unassigned-openly-tasks"],
  ViewId["operations-coordinator"],
  ViewId["standard-quoting"],
  ViewId["ingestion-review"],
  ViewId["upsell-quoting"],
  ViewId["live-quoting"],
  ViewId["insurance-quoting"],
  ViewId["estimating"],
  ViewId["estimating-approvals"],
  ViewId["my-estimating-tasks"],
  ViewId["my-blocking-tasks"],
  ViewId["quote-review"],
  ViewId["intro-call"],
  ViewId["pre-production"],
  ViewId["contractor-production"],
  ViewId["contractor-dash"],
  ViewId["finalCheckOff"],
  ViewId["live-photo-queue"],
  ViewId.finance,
  ViewId.permitting,
  ViewId["change-orders-submission"],
  ViewId["post-install-issues"],
  ViewId["blocking-tasks"],
  ViewId["upsells"],
  ViewId["review-financial-adjustments"],
  ViewId["remi-labor"],
  ViewId["enterprise-management"],
  ViewId["my-assignments"],
  ViewId["org-assignments"],
  ViewId["1099-estimating"],
  ViewId["itel-tasks"],
] as const;

export const DateRangeSchema = z.object({
  from: z.date(),
  to: z.date().optional(),
});

export type DateRange = z.infer<typeof DateRangeSchema>;

const StringArrayFilterComparatorSchema = z.enum(["in", "not_in", "is_null", "is_not_null"]);
export type StringArrayFilterComparator = z.infer<typeof StringArrayFilterComparatorSchema>;

export const NULL_STRING_ARRAY_COMPARATORS = [
  "is_null",
  "is_not_null",
] as const satisfies StringArrayFilterComparator[];

// Region is a spatial-containment probe with no nullable column, so a null comparator is meaningless for it.
export const NULL_COMPARATOR_UNSUPPORTED_FILTER_IDS = ["region"] as const;

const DateRangeFilterComparatorSchema = z.enum([
  "between",
  "gt",
  "lt",
  "is_between",
  "is_null",
  "is_not_null",
  "relative",
]);
export type DateRangeFilterComparator = z.infer<typeof DateRangeFilterComparatorSchema>;

const DateRangeFilterSchema = z.object({
  filterId: z.string(),
  type: z.literal("DATE_RANGE"),
  value: DateRangeSchema.optional(),
  comparator: DateRangeFilterComparatorSchema.optional(),
  relativeDays: z.int().positive().optional(),
});

const StringArrayFilterSchema = z.object({
  filterId: z.string(),
  type: z.literal("STRING_ARRAY"),
  value: z.array(z.string()),
  comparator: StringArrayFilterComparatorSchema.optional(),
});

const BooleanFilterSchema = z.object({
  filterId: z.string(),
  type: z.literal("BOOLEAN"),
  value: z.boolean().optional(),
});

export type StringArrayFilter = z.infer<typeof StringArrayFilterSchema>;

const FilterSchema = discriminatedUnion("type", [DateRangeFilterSchema, StringArrayFilterSchema, BooleanFilterSchema]);
export type Filter = z.infer<typeof FilterSchema>;

const ColumnSchema = z.object({ hide: z.boolean().optional() });
export type Column = z.infer<typeof ColumnSchema>;

const SortSchema = z.object({ id: z.string(), desc: z.boolean() });
export type Sort = z.infer<typeof SortSchema>;

export const TableViewStateSchema = z.object({
  filters: z.record(z.string(), FilterSchema),
  columns: z.record(z.string(), ColumnSchema),
  columnOrder: z.array(z.string()).optional(),
  pinnedColumnCount: z.number().optional(),
  columnSizing: z.record(z.string(), z.number()).optional(),
  sort: z.array(SortSchema),
  groupBy: z.string().optional(),
  viewId: ViewIdSchema,
});

export type TableViewState = z.infer<typeof TableViewStateSchema>;
